import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import atmosphere from 'atmosphere.js';
import { showLossLimitMessage } from '~/components/account/limits/ducks/index.js';
import { validateSession } from '~/components/authentication/ducks/index.js';
import AtmosphereRequest from '~/components/authentication/atmosphere-request.ts';
import {
	accountUpdate,
	setCasinoNetPosition,
	updateGermanyRemainingBonusValue,
	selectCurrency,
	selectLocale,
	selectClosingAccountStatus,
	setLugasActivationTime
} from '~/components/account/ducks/index.js';
import { showPhoneVerificationModal } from '~/components/account/phone-verification-modal/phone-verification-modal-duck.js';
import useConfiguration from '~/configuration/use-configuration.js';
import { showNotification, showBinaryQuestion } from './notifications/ducks/notifications.js';
import { showOneClickOptInMessage, showBonusMaxStakeMessage } from './bonus/ducks/index';
import { selectIsGermanRegulatedFranchise, selectIsMentorRealTimeAlertsEnabled } from '~/components/brand/ducks';
import { setIsWinAnimationShown, setWinAnimationData } from '~/components/casino-shared/inhouse-jackpot/ducks/index.js';
import { availableBonusUpdate } from '~/components/promotions/ducks/promotions-duck';
import { setRankMessage } from '~/components/casino-shared/tournaments/ducks';
import {
	setIsInsufficientCasinoBalanceModalShown,
	setIsBalanceLimitExceededModalShown
} from '~/components/casino-shared/ducks';
import { showRCPopup, startRCAfterCoolOff } from '~/components/account/reality-check/ducks/index.js';
import { requestApi } from '~/services/api-new';
import {
	APP_EVENTS,
	WEB_EVENTS,
	GAMEPLAY_BLOCK_TYPES,
	registerAppEventCallback,
	unregisterAppEventCallback,
	sendAppBridgeEvent,
	showNativeNotification,
	NOTIFICATION_TYPE
} from '~/components/app-bridge/index.js';
import {
	isNativeWebSocketEnabled,
	isNativeGamePlayBlockEnabled,
	isNativeMentorMessageEnabled,
	isNativeNotificationEnabled
} from '~/components/app-bridge/app-properties.js';
import { showSupportDialogBox } from '~/components/support/ducks/index.js';
import { useRouter } from '~/utils/navigation';
import { ROUTE_HOME, ROUTE_ACCOUNT_DEPOSIT } from '~/components/router/constants';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import { showQueuedNotification } from './notifications/ducks/actions';
import useTranslations from '~/components/translation/use-translations';
import currencyFormatter from '~/libs/currency-formatter';

import { FlowType, spendingLimitStoragekey, trackSpendingLimitEvent } from './limits/spending-limit/utils/tracking';
import {
	ndlEventStream,
	NOTIFICATION_ID,
	NotificationEvents,
	NotificationStatus as KreditzNotificationStatus
} from './limits/spending-limit/utils/constant';
import { LIMITS_REMAINING_AMOUNT_ENDPOINT } from './limits/constants';

const DEV = process.env.NODE_ENV !== 'production';
const DEBUG = process.env.NODE_ENV === 'debug';
const WEBSOCKET_SERVER_AVAILABLE = !DEV || DEBUG;

const socket = atmosphere;

const TEXT_KEYS = [
	'BALANCE_LIMIT_REACHED_TITLE',
	'USER_BALANCE_REACHED_MESSAGE',
	'MOBILE_SPENDING_LIMIT_UPDATED',
	'MOBILE_HEADER_BAR_DEPOSIT',
	'MOBILE_SPENDING_LIMIT_VERIFICATION_SUCCESS_NEW_LIMIT',
	'MOBILE_GENERAL_ERROR',
	'MOBILE_SPENDING_LIMIT_VERIFICATION_FAILED'
];

const SOCKET_STATUS = {
	NOT_INITIALISED: 'not-initialised',
	INITIALISED: 'initialised',
	ERROR: 'error'
};

const MAX_RECONNECT_ATTEMPTS = 15;
const RECONNECTION_DELAY = 1000; // 1 seconds

const WebSocket = () => {
	const dispatch = useDispatch();
	const { hasWebsocketNotificationMessageSupport, timerForMentorRealTimeAlerts } = useConfiguration();
	const { ROUTE_CASINO_EXPLORE } = useCasinoRoutes();

	const authenticated = useSelector(state => state.authentication.authenticated);
	const isGermanyRegulatedFranchise = useSelector(selectIsGermanRegulatedFranchise);
	const mentorRealTimeAlertsEnabled = useSelector(selectIsMentorRealTimeAlertsEnabled);
	const isClosingAccount = useSelector(selectClosingAccountStatus);

	// useRouter causes unnecessary rerenders due to react router context changes on every route click.
	// This issue causes our websocket subscription to reconnect on every route change.
	// To get around this we wrap the router method in a ref
	const router = useRouter();
	const routerCurrent = useRef(router).current;

	const [status, setStatus] = useState(SOCKET_STATUS.NOT_INITIALISED);
	const statusUpdateRef = useRef(null);
	const reconnectAttemptsRef = useRef(0);

	const currency = useSelector(selectCurrency);
	const locale = useSelector(selectLocale);
	const { translations: t, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	const showSpendingLimitNotification = async (notificationStatus, message) => {
		const flow = sessionStorage.getItem(spendingLimitStoragekey);

		const data = await requestApi({ endpoint: LIMITS_REMAINING_AMOUNT_ENDPOINT });
		const maxOperation = Number(data.PARAM_MAX_OPERATION);

		const showDepositButton =
			maxOperation && notificationStatus === KreditzNotificationStatus.completed && flow === FlowType.popup;

		const options = showDepositButton
			? {
					buttonText: t.MOBILE_HEADER_BAR_DEPOSIT,
					buttonCB: () => {
						trackSpendingLimitEvent({ flow, label: 'Click deposit bank' });
						routerCurrent.push(ROUTE_ACCOUNT_DEPOSIT);
					}
				}
			: {};

		if (isNativeNotificationEnabled()) {
			const type = showDepositButton
				? NOTIFICATION_TYPE.SPENDING_LIMIT_WITH_DEPOSIT_BUTTON
				: NOTIFICATION_TYPE.SPENDING_LIMIT;

			showNativeNotification(type, message);
		} else {
			dispatch(
				showQueuedNotification({
					autoHide: true,
					openInSidebar: true,
					id: NOTIFICATION_ID,
					message,
					color: notificationStatus === KreditzNotificationStatus.completed ? 'green' : 'red',
					...options
				})
			);
		}
	};

	const handleWebSocketMessage = useCallback(
		webSocketMessage => {
			try {
				const handlers = {
					'player.logout': payload => {
						if (!isClosingAccount) {
							dispatch(
								validateSession({
									signoutMessage: { message: payload.message }
								})
							);
						}
					},

					/**
					 * @param {Object} payload
					 * @param {number} payload.playerId
					 * @param {string} payload.locale
					 * @param {number} payload.totalBalance
					 * @param {number} payload.freeBalance
					 * @param {number} payload.lockedBalance
					 * @param {string} payload.formattedBalance
					 * @param {string} payload.currencyCode
					 * @param {any} payload.countryId
					 * @param {any} payload.activeBonuses
					 * @param {boolean} payload.hasAvailableBonuses
					 */
					'player.update': payload => {
						const res = {};
						if (payload.totalBalance !== undefined) {
							res.balance = payload.totalBalance;
						} else if (payload.playerPoints === undefined) {
							res.balance = 0;
						}
						if (payload.playerPoints !== undefined) {
							res.points = payload.playerPoints;
						}

						dispatch(
							accountUpdate({
								...payload,
								...res
							})
						);
						if (isGermanyRegulatedFranchise) {
							dispatch(updateGermanyRemainingBonusValue());
						}
					},
					'player.bonus.update': payload => {
						dispatch(availableBonusUpdate(payload));
					},
					'player.bonus.exclusion': payload => {
						dispatch(
							accountUpdate({
								playerSelfExcluded: payload.isBonusBlocked,
								playerBonusBlocked: payload.isSelfExcluded === false
							})
						);
					},
					'player.verifyMobile': payload => {
						dispatch(showPhoneVerificationModal(payload));
					},
					'opt.in.confirmation': payload => {
						dispatch(showOneClickOptInMessage(payload));
					},
					'bonus.max.stake.message': payload => {
						dispatch(showBonusMaxStakeMessage(payload));
					},
					'player.autoWithdraw': payload => {
						if (payload !== undefined) {
							const { ibanMissing = false, tempAccountPlayer = false } = payload;
							dispatch(
								accountUpdate({
									autoWithdraw: {
										hasAutoWithdrawWin: true,
										ibanMissing,
										tempAccountPlayer
									}
								})
							);
						}
					},
					'player.limitedBalanceAutoWithdrawal': payload => {
						if (payload) {
							const { amount, reference } = payload;
							const message = `${t.BALANCE_LIMIT_REACHED_TITLE} - ${currencyFormatter.format(
								Number(amount),
								currency,
								locale
							)} ${t.USER_BALANCE_REACHED_MESSAGE}`;

							if (isNativeNotificationEnabled()) {
								showNativeNotification(NOTIFICATION_TYPE.LIMITED_BALANCE_AUTO_WD, message);
							} else {
								setTimeout(
									() =>
										dispatch(
											showQueuedNotification({
												message,
												color: 'neutral',
												autoHide: true,
												autoHideDuration: 5000,
												id: `${reference}-${amount}`
											})
										),
									1000
								);
							}
						}
					},
					'player.limits.spend.update': payload => {
						if (payload && payload?.amount) {
							const { amount } = payload;
							const message = t.MOBILE_SPENDING_LIMIT_UPDATED.replace(
								'__amount__',
								currencyFormatter.format(Number(amount), currency, locale)
							);

							const flow = sessionStorage.getItem(spendingLimitStoragekey);

							if (flow) {
								trackSpendingLimitEvent({
									flow,
									label: 'Upload docs successful limits applied'
								});
							}

							if (isNativeNotificationEnabled()) {
								showNativeNotification(NOTIFICATION_TYPE.SPENDING_LIMIT_WITH_DEPOSIT_BUTTON, message);
							} else {
								dispatch(
									showQueuedNotification({
										message,
										color: 'green',
										autoHide: true,
										buttonText: t.MOBILE_HEADER_BAR_DEPOSIT,
										buttonCB: () => {
											trackSpendingLimitEvent({
												flow,
												label: 'Click deposit doc'
											});
											routerCurrent.push(ROUTE_ACCOUNT_DEPOSIT);
										},
										id: `approved-${amount}`
									})
								);
							}
						}
					},
					'player.message': payload => {
						if (hasWebsocketNotificationMessageSupport) {
							dispatch(
								showNotification({
									title: payload.title,
									message: payload.message,
									color: 'green',
									autoHide: true
								})
							);
						}
					},
					'player.mentor.message': payload => {
						if (!mentorRealTimeAlertsEnabled) return false;
						const mentorMessage = payload.message;

						if (!isNativeMentorMessageEnabled()) {
							dispatch(
								showNotification({
									titleKey: mentorMessage?.data?.alertCodeTitleTextKey,
									messageKey: mentorMessage?.data?.alertCodeMessageTextKey,
									color: 'neutral',
									autoHide: true,
									autoHideDuration: timerForMentorRealTimeAlerts || 7000
								})
							);
						} else {
							// Send the notification to mobile apps
							sendAppBridgeEvent(WEB_EVENTS.SHOW_REALTIME_MENTOR_MESSAGE, [
								{
									mentorMessage: mentorMessage?.data
								}
							]);
						}
					},
					'loss.limit.checking.message': payload => {
						dispatch(
							showLossLimitMessage({
								show: true,
								timeUnit: payload.timeUnit,
								maxPlayableAmount: payload.maxPlayableAmount,
								currency: payload.currency
							})
						);
					},
					'lugas.error.message': payload => {
						dispatch(
							showBinaryQuestion({
								headerKey: `${payload.errorType}_HEADER`,
								messageKey: `${payload.errorType}_MESSAGE`
							})
						);
					},
					'lugas.activated': payload => {
						if (payload) {
							dispatch(setLugasActivationTime(payload));
						}
					},
					'jackpot.inhouse.win': payload => {
						dispatch(setWinAnimationData(payload));
						dispatch(setIsWinAnimationShown(true));
					},
					'tournament.leaderboard': payload => {
						dispatch(setRankMessage(payload));
					},
					'insufficient.casino.balance': () => {
						dispatch(setIsInsufficientCasinoBalanceModalShown(true));
					},
					'player.realityCheck': payload => {
						dispatch(
							showRCPopup({
								duration: payload.duration,
								playTimeHours: payload.playTimeHours,
								playTimeMinutes: payload.playTimeMinutes,
								betSum: payload.betSum,
								totalWagered: payload.totalWagered,
								totalWon: payload.totalWon,
								notLosing: payload.notLosing
							})
						);
						// eslint-disable-next-line no-console
						console.info('Reality Check Message from websocket', payload);
					},
					'player.realityCheck.coolOff': () => {
						dispatch(startRCAfterCoolOff());
						// eslint-disable-next-line no-console
						console.info('Reality Check Cool Off Message from websocket');
					},
					'balance.limit.message': () => {
						dispatch(setIsBalanceLimitExceededModalShown(true));
					},
					'player.game.play.not.allowed': () => {
						if (isNativeGamePlayBlockEnabled()) {
							sendAppBridgeEvent(WEB_EVENTS.SHOW_GAMEPLAY_BLOCK_MESSAGE, [
								{
									gamePlayBlock: {
										type: GAMEPLAY_BLOCK_TYPES.NOT_ALLOWED
									}
								}
							]);
						} else {
							dispatch(
								showNotification({
									color: 'neutral',
									messageKey: 'GAMEPLAY_NOT_ALLOWED_ERROR',
									autoHide: true,
									autoHideDuration: 7000,
									onClose: () => {
										// to prevent multiple redirection. when autoHideDuration finished, onClose callback
										// triggered again even notification is already closed by user
										if (window.location.pathname?.includes('/casino/game')) {
											routerCurrent.push(ROUTE_CASINO_EXPLORE);
										}
									},
									onCloseClickCallback: () => {
										routerCurrent.push(ROUTE_CASINO_EXPLORE);
									}
								})
							);
						}
					},
					'product.block.error.message': payload => {
						switch (payload?.productBlock?.productType) {
							case 'casino':
								if (isNativeGamePlayBlockEnabled()) {
									sendAppBridgeEvent(WEB_EVENTS.SHOW_GAMEPLAY_BLOCK_MESSAGE, [
										{
											gamePlayBlock: {
												type: GAMEPLAY_BLOCK_TYPES.PRODUCT_BLOCKED,
												product: payload.productBlock.productType
											}
										}
									]);
								} else {
									dispatch(
										showNotification({
											color: 'neutral',
											messageKey: 'NL_CASINO_PRODUCT_BLOCKED_ERROR',
											buttonTextKey: 'NL_CASINO_PRODUCT_BLOCKED_ERROR_BTN_TEXT',
											autoHide: true,
											autoHideDuration: 7000,
											onClose: () => {
												// to prevent multiple redirection. when autoHideDuration finished, onClose callback
												// triggered again even notification is already closed by user
												if (window.location.pathname?.includes('/casino/game')) {
													routerCurrent.push(ROUTE_HOME);
												}
											},
											onCloseClickCallback: () => {
												routerCurrent.push(ROUTE_HOME);
											},
											buttonCB: () => {
												dispatch(showSupportDialogBox(true));
												routerCurrent.push(ROUTE_HOME);
											}
										})
									);
								}
								break;
							case 'sportsbook':
								// Do not display for Sportsbook as it is handled in betslip
								break;
							default:
						}
					},
					'kreditz.response.notification': payload => {
						const { status: kreditzStatus, limit } = payload;

						const notificationStatus = kreditzStatus?.toLowerCase();
						const flow = sessionStorage.getItem(spendingLimitStoragekey);

						if (notificationStatus) {
							ndlEventStream.emit(NotificationEvents.notification, {
								status: notificationStatus,
								limit
							});

							let message = '';

							if (notificationStatus === KreditzNotificationStatus.completed) {
								message = `${t.MOBILE_SPENDING_LIMIT_VERIFICATION_SUCCESS_NEW_LIMIT} ${limit}.`;

								trackSpendingLimitEvent({ flow, label: 'Bank verification successful limits applied' });
							} else {
								message =
									notificationStatus === KreditzNotificationStatus.failed
										? t.MOBILE_GENERAL_ERROR
										: t.MOBILE_SPENDING_LIMIT_VERIFICATION_FAILED;

								trackSpendingLimitEvent({ flow, label: 'Bank verification failed' });
							}

							showSpendingLimitNotification(notificationStatus, message);
						}
					},
					casinoNetPosition: payload => {
						if (payload) {
							dispatch(setCasinoNetPosition(payload));
						}
					}
				};
				Object.entries(webSocketMessage).forEach(([key, value]) => {
					if (DEV || DEBUG) {
						// eslint-disable-next-line no-console
						console.debug('Websocket message received');
						// eslint-disable-next-line no-console
						console.dir(key, value);
					}

					if (handlers[key]) {
						handlers[key](value);
					} else if (DEV || DEBUG) {
						// eslint-disable-next-line no-console
						console.error(`Websocket message handler for ${key} is not available`);
						// eslint-disable-next-line no-console
						console.dir(...webSocketMessage);
					}
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log(
					`An error occurred while handling the websocket message: ${webSocketMessage}; Error: ${error}`
				);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			dispatch,
			isGermanyRegulatedFranchise,
			t,
			currency,
			locale,
			hasWebsocketNotificationMessageSupport,
			mentorRealTimeAlertsEnabled,
			timerForMentorRealTimeAlerts,
			routerCurrent,
			ROUTE_CASINO_EXPLORE,
			isClosingAccount
		]
	);

	/*
	 * Stabilising the reference of handleWebSocketMessage to avoid re-subscribing on every render
	 */
	const messageHandlerRef = useRef(handleWebSocketMessage);

	/**
	 * Update the ref to the latest version of handleWebSocketMessage
	 * This ensures that the most recent callback is used in the subscription
	 */
	useEffect(() => {
		messageHandlerRef.current = handleWebSocketMessage;
	}, [handleWebSocketMessage]);

	const subscribe = useCallback(() => {
		setStatus(SOCKET_STATUS.INITIALISED);
		socket.unsubscribe();

		const request = AtmosphereRequest(
			response => {
				try {
					const message = JSON.parse(response.responseBody);
					messageHandlerRef.current(message);
				} catch (_) {
					/**
					 * Removing logging of parsing errors as they are not actionable and clutter the console
					 */
				}
			},
			/**
			 * onError, onClose and onClientClose are handled here
			 */
			() => {
				/*
				 * Debounce multiple status updates within RECONNECTION_DELAY seconds
				 */
				clearTimeout(statusUpdateRef.current);

				if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
					statusUpdateRef.current = setTimeout(() => {
						reconnectAttemptsRef.current += 1;

						setStatus(SOCKET_STATUS.NOT_INITIALISED);
					}, RECONNECTION_DELAY);
				}
			}
		);

		socket.subscribe(request);
	}, []);

	/*
	 * MOBILE APP RELATED EVENTS
	 */
	useEffect(() => {
		if (!isNativeWebSocketEnabled()) {
			return;
		}

		if (authenticated) {
			registerAppEventCallback(APP_EVENTS.WEBSOCKET_MESSAGE, handleWebSocketMessage);
		} else {
			unregisterAppEventCallback(APP_EVENTS.WEBSOCKET_MESSAGE);
		}
	}, [authenticated, handleWebSocketMessage]);

	useEffect(() => {
		const websocketAvailable =
			authenticated &&
			!isNativeWebSocketEnabled() &&
			!isLoadingTranslations &&
			WEBSOCKET_SERVER_AVAILABLE &&
			status === SOCKET_STATUS.NOT_INITIALISED;

		if (websocketAvailable) {
			subscribe();
		}
	}, [authenticated, isLoadingTranslations, status, subscribe]);

	/*
	 * Cleanup on UnMount
	 */
	useEffect(() => {
		return () => {
			socket.unsubscribe();
			clearTimeout(statusUpdateRef.current);
			setStatus(SOCKET_STATUS.NOT_INITIALISED);

			if (isNativeWebSocketEnabled()) {
				unregisterAppEventCallback(APP_EVENTS.WEBSOCKET_MESSAGE);
			}
		};
	}, []);

	return null;
};

export default WebSocket;
